import React, { useState, useEffect, FC } from 'react';
import { useSelector } from 'react-redux';
import { Dimmer } from 'semantic-ui-react';
import { uniqWith, omit, isEqual } from 'lodash';
import {
  Dealer,
  YearMakeModelConfig,
} from '@upstart/harmony/lib/legos/dealerTypes';
import DecodeService from '../../services/DecodeService';
import './SignAndDrive.scss';

interface SignAndDriveProps {
  formData: YearMakeModelConfig[];
  onChange: (yearMakeModelConfigs: YearMakeModelConfig[]) => {};
}

interface SelectedMakeModel {
  year?: number;
  make?: string;
  model?: string;
  matchAnyMakeModel?: boolean;
}

export const SignAndDrive: FC<SignAndDriveProps> = ({ formData, onChange }) => {
  // Need to type state object properly (big task). Using any for now
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dealer: Dealer = useSelector((state: any) => state.dealers.dealer);
  const [yearMakeModelConfigs, setYearMakeModelConfigs] = useState(
    formData || [],
  );
  const [recentModels, setRecentModels] = useState<
    { year: number; make: string; model: string }[]
  >([]);
  const [selectedMakeModel, setSelectedMakeModel] = useState<SelectedMakeModel>(
    { matchAnyMakeModel: true },
  );
  const [selectedYear, setSelectedYear] = useState<{
    year?: number;
    matchAnyYear?: boolean;
  }>({ matchAnyYear: true });
  const isAddInceptionFeesToTotalAtStartEnabled = !!dealer.desking
    ?.addInceptionFeesToTotalAtStart;

  useEffect(() => {
    async function getRecentModels() {
      const models = await DecodeService.getRecentModels(
        dealer.brand?.join(','),
      );
      setRecentModels(models);
    }
    getRecentModels();
  }, [dealer.brand]);

  const getYearOptions = (thisSelectedMakeModel?: SelectedMakeModel) => {
    if (thisSelectedMakeModel?.matchAnyMakeModel) {
      return uniqWith(
        recentModels.map(recentModel => omit(recentModel, ['make', 'model'])),
        isEqual,
      ).map(recentModel => (
        <option key={recentModel.year} value={JSON.stringify(recentModel)}>
          {recentModel.year}
        </option>
      ));
    }
    return recentModels
      .filter(
        recentModel =>
          recentModel.make === thisSelectedMakeModel?.make &&
          recentModel.model === thisSelectedMakeModel?.model,
      )
      .map(recentModel => (
        <option key={recentModel.year} value={JSON.stringify(recentModel)}>
          {recentModel.year}
        </option>
      ));
  };

  const onAdd = () => {
    if (!isAddInceptionFeesToTotalAtStartEnabled) {
      return;
    }
    const newYearMakeModelConfigs = [
      ...yearMakeModelConfigs,
      { ...selectedMakeModel, ...selectedYear },
    ];
    setYearMakeModelConfigs(newYearMakeModelConfigs);
    onChange(newYearMakeModelConfigs);
    // Reset select inputs after adding
    setSelectedMakeModel({ matchAnyMakeModel: true });
  };

  const onChangeYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(JSON.parse(e.target.value));
  };

  const getMakeModelOptions = () =>
    uniqWith(
      recentModels.map(recentModel => omit(recentModel, 'year')),
      isEqual,
    ).map(recentModel => {
      const makeModel = `${recentModel.make} ${recentModel.model}`;
      return (
        <option key={makeModel} value={JSON.stringify(recentModel)}>
          {makeModel}
        </option>
      );
    });

  const onChangeMakeModel = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMakeModel(JSON.parse(e.target.value));
    setSelectedYear({ matchAnyYear: true });
  };

  // Remove configuration and update parent form so Save button works correctly
  const onRemoveConfig = (index: number) => {
    if (!isAddInceptionFeesToTotalAtStartEnabled) {
      return;
    }
    const newYearMakeModelConfigs = yearMakeModelConfigs.filter(
      (_, innerIndex) => innerIndex !== index,
    );
    setYearMakeModelConfigs(newYearMakeModelConfigs);
    onChange(newYearMakeModelConfigs);
  };

  return (
    <>
      {!isAddInceptionFeesToTotalAtStartEnabled && (
        <>
          <div className="sign-drive-header">
            Please enable &quot;Add Inception Fees to Total At Start by
            default&quot; in Desking Configuration section first to use this
            feature
          </div>
          <br />
          <br />
        </>
      )}
      {dealer?.isIndependent && (
        <>
          <div className="sign-drive-header">
            This feature is not available for independent dealers.
          </div>
          <br />
          <br />
        </>
      )}
      <Dimmer.Dimmable
        dimmed={
          !isAddInceptionFeesToTotalAtStartEnabled || dealer?.isIndependent
        }
        blurring
      >
        <div className="sign-drive-header">Please Add A Vehicle</div>
        <div>
          Please select a model and year to remove the first month&apos;s
          payment from all customer leases for that specific vehicle
        </div>
        <div className="year-make-model-entry-container entry-header">
          <div>Model</div>
          <div className="year-button">
            <div>Year</div>
            <div> </div>
          </div>
        </div>
        {!yearMakeModelConfigs.length && (
          <div className="year-make-model-entry-container entry-row text-muted">
            (No options added yet - select one Model Year combo below and click
            &quot;Add&quot;)
          </div>
        )}
        {!!yearMakeModelConfigs.length &&
          yearMakeModelConfigs.map((entry, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="year-make-model-entry-container entry-row"
            >
              <div>
                {entry.matchAnyMakeModel
                  ? 'All'
                  : `${entry.make} ${entry.model}`}
              </div>
              <div className="year-button">
                <div>{entry.matchAnyYear ? 'All' : entry.year}</div>
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={() => onRemoveConfig(index)}
                >
                  &nbsp;X&nbsp;
                </button>
              </div>
            </div>
          ))}
        <br />
        <br />
        <br />
        <div className="year-make-model-entry-container entry-row">
          <select onChange={onChangeMakeModel}>
            <option
              key={'All'}
              value={'{ "matchAnyMakeModel": true }'}
              selected={isEqual(selectedMakeModel, { matchAnyMakeModel: true })}
            >
              All
            </option>
            {getMakeModelOptions()}
          </select>
          <div className="year-button">
            <select onChange={onChangeYear}>
              <option
                key={'All'}
                value={'{ "matchAnyYear": true }'}
                selected={isEqual(selectedYear, { matchAnyYear: true })}
              >
                All
              </option>
              {getYearOptions(selectedMakeModel)}
            </select>
            <button className="btn btn-primary" type="button" onClick={onAdd}>
              Add
            </button>
          </div>
        </div>
      </Dimmer.Dimmable>
    </>
  );
};

export default SignAndDrive;
